// Slice de UI da feature management: período, loja, sort do leaderboard.
// RF-26: filtro global unificado — presets (today/7d/15d/30d/90d) OU intervalo
// custom (from/to date-only). `customRange` não-nulo ⇒ intervalo custom ativo.
import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DateRange } from '@/lib/filters/period';
import type { LeaderboardSort, ManagementPeriod, PeriodRange } from './api';

/** date-only YYYY-MM-DD; alias do `DateRange` compartilhado. */
export type ManagementCustomRange = DateRange;

export interface ManagementUiState {
  selectedPeriod: ManagementPeriod;
  /** Não-nulo ⇒ intervalo custom ativo (sobrepõe o preset nas queries). */
  customRange: ManagementCustomRange | null;
  selectedStoreId: string;
  selectedSort: LeaderboardSort;
}

const initialState: ManagementUiState = {
  selectedPeriod: '7d',
  customRange: null,
  selectedStoreId: 'all',
  selectedSort: 'vendas',
};

const slice = createSlice({
  name: 'managementUi',
  initialState,
  reducers: {
    // Escolher um preset sai do modo custom.
    managementPeriodChanged(state, action: PayloadAction<ManagementPeriod>) {
      state.selectedPeriod = action.payload;
      state.customRange = null;
    },
    managementCustomRangeChanged(state, action: PayloadAction<ManagementCustomRange>) {
      state.customRange = action.payload;
    },
    managementStoreChanged(state, action: PayloadAction<string>) {
      state.selectedStoreId = action.payload;
    },
    managementSortChanged(state, action: PayloadAction<LeaderboardSort>) {
      state.selectedSort = action.payload;
    },
  },
});

export const {
  managementPeriodChanged,
  managementCustomRangeChanged,
  managementStoreChanged,
  managementSortChanged,
} = slice.actions;
export const managementUiReducer = slice.reducer;

export const selectManagementPeriod = (s: { managementUi: ManagementUiState }): ManagementPeriod =>
  s.managementUi.selectedPeriod;
export const selectManagementCustomRange = (s: {
  managementUi: ManagementUiState;
}): ManagementCustomRange | null => s.managementUi.customRange;
export const selectManagementStore = (s: { managementUi: ManagementUiState }): string =>
  s.managementUi.selectedStoreId;
export const selectManagementSort = (s: { managementUi: ManagementUiState }): LeaderboardSort =>
  s.managementUi.selectedSort;

/**
 * Filtro de período resolvido para as queries (RF-26): quando há `customRange`,
 * manda `from`/`to` (envelope volta `period:'custom'`); senão só o preset.
 *
 * MEMOIZADO de propósito. Este seletor MONTA um objeto; sem memoização ele
 * devolve uma referência nova a cada chamada, e o `useSyncExternalStore` do
 * react-redux entende "mudou" a CADA notificação da store — re-renderizando todo
 * componente que o lê, a cada dispatch de qualquer lugar. Onde há dispatch em
 * efeito (o painel de rede sincroniza filtro↔URL e auto-seleciona a loja única),
 * isso vira realimentação e estoura em `Maximum update depth exceeded`.
 * Com `createSelector`, o mesmo estado devolve a MESMA referência.
 */
export const selectManagementPeriodRange = createSelector(
  [selectManagementPeriod, selectManagementCustomRange],
  (period, customRange): PeriodRange =>
    customRange ? { period, from: customRange.from, to: customRange.to } : { period },
);
